import { calculateMonthlyRampedQuotas } from "./calculationUtils";
import { CalculatorAction, CalculatorReducerState } from "./types";

// Define initial values for calculator
const initialBaseSalary = 75000;
const initialTargetCommission = 100000;
const initialCommissionRate = 10;
const initialRampMonths1to3 = 25;
const initialRampMonths4to6 = 50;
const initialRampMonths7to9 = 75;
const initialRampMonths10to12 = 100;

// Calculate initial derived values
const initialAnnualQuota = Math.round(
  initialTargetCommission / (initialCommissionRate / 100),
);
const initialMonthlyRampedQuotas = calculateMonthlyRampedQuotas(
  initialAnnualQuota,
  initialRampMonths1to3,
  initialRampMonths4to6,
  initialRampMonths7to9,
  initialRampMonths10to12,
);

export const initialCalculatorState: CalculatorReducerState = {
  baseSalary: initialBaseSalary,
  targetCommission: initialTargetCommission,
  commissionRate: initialCommissionRate,
  rampMonths1to3: initialRampMonths1to3,
  rampMonths4to6: initialRampMonths4to6,
  rampMonths7to9: initialRampMonths7to9,
  rampMonths10to12: initialRampMonths10to12,
  guaranteeMonths1to3: 100,
  guaranteeMonths4to6: 50,
  // Default with just one accelerator (100% threshold, 1.5x multiplier)
  accelerators: [{ threshold: 100, multiplier: 1.5 }],
  monthlyPerformances: [0, 0, 25, 50, 75, 100, 100, 100, 100, 100, 100, 100],
  ote: initialBaseSalary + initialTargetCommission,
  monthlyRampedQuotas: initialMonthlyRampedQuotas,
};

/**
 * Reducer function for the calculator
 */
export function calculatorReducer(
  state: CalculatorReducerState,
  action: CalculatorAction,
): CalculatorReducerState {
  switch (action.type) {
    case "SET_BASE_SALARY": {
      // Handle both direct values and functional updates
      const newBaseSalary =
        typeof action.payload === "function"
          ? action.payload(state.baseSalary)
          : action.payload;

      return {
        ...state,
        baseSalary: newBaseSalary,
        ote: newBaseSalary + state.targetCommission,
      };
    }

    case "SET_TARGET_COMMISSION": {
      // Handle both direct values and functional updates
      const newTargetCommission =
        typeof action.payload === "function"
          ? action.payload(state.targetCommission)
          : action.payload;

      return {
        ...state,
        targetCommission: newTargetCommission,
        ote: state.baseSalary + newTargetCommission,
      };
    }

    case "SET_COMMISSION_RATE": {
      // Handle both direct values and functional updates
      const newCommissionRate =
        typeof action.payload === "function"
          ? action.payload(state.commissionRate)
          : action.payload;

      // When commission rate changes, we need to update the monthly ramped quotas
      const newState = {
        ...state,
        commissionRate: newCommissionRate,
      };

      // Calculate the annual quota
      const annualQuota =
        newCommissionRate > 0
          ? Math.round(state.targetCommission / (newCommissionRate / 100))
          : 0;

      // Calculate the monthly ramped quotas
      const monthlyRampedQuotas = calculateMonthlyRampedQuotas(
        annualQuota,
        state.rampMonths1to3,
        state.rampMonths4to6,
        state.rampMonths7to9,
        state.rampMonths10to12,
      );

      return {
        ...newState,
        monthlyRampedQuotas,
      };
    }

    case "SET_RAMP_MONTHS_1_TO_3": {
      const newValue =
        typeof action.payload === "function"
          ? action.payload(state.rampMonths1to3)
          : action.payload;

      // Calculate the annual quota
      const annualQuota =
        state.commissionRate > 0
          ? Math.round(state.targetCommission / (state.commissionRate / 100))
          : 0;

      // Calculate the updated monthly ramped quotas with the new ramp value
      const monthlyRampedQuotas = calculateMonthlyRampedQuotas(
        annualQuota,
        newValue,
        state.rampMonths4to6,
        state.rampMonths7to9,
        state.rampMonths10to12,
      );

      return {
        ...state,
        rampMonths1to3: newValue,
        monthlyRampedQuotas,
      };
    }

    case "SET_RAMP_MONTHS_4_TO_6": {
      const newValue =
        typeof action.payload === "function"
          ? action.payload(state.rampMonths4to6)
          : action.payload;

      // Calculate the annual quota
      const annualQuota =
        state.commissionRate > 0
          ? Math.round(state.targetCommission / (state.commissionRate / 100))
          : 0;

      // Calculate the updated monthly ramped quotas with the new ramp value
      const monthlyRampedQuotas = calculateMonthlyRampedQuotas(
        annualQuota,
        state.rampMonths1to3,
        newValue,
        state.rampMonths7to9,
        state.rampMonths10to12,
      );

      return {
        ...state,
        rampMonths4to6: newValue,
        monthlyRampedQuotas,
      };
    }

    case "SET_RAMP_MONTHS_7_TO_9": {
      const newValue =
        typeof action.payload === "function"
          ? action.payload(state.rampMonths7to9)
          : action.payload;

      // Calculate the annual quota
      const annualQuota =
        state.commissionRate > 0
          ? Math.round(state.targetCommission / (state.commissionRate / 100))
          : 0;

      // Calculate the updated monthly ramped quotas with the new ramp value
      const monthlyRampedQuotas = calculateMonthlyRampedQuotas(
        annualQuota,
        state.rampMonths1to3,
        state.rampMonths4to6,
        newValue,
        state.rampMonths10to12,
      );

      return {
        ...state,
        rampMonths7to9: newValue,
        monthlyRampedQuotas,
      };
    }

    case "SET_RAMP_MONTHS_10_TO_12": {
      const newValue =
        typeof action.payload === "function"
          ? action.payload(state.rampMonths10to12)
          : action.payload;

      // Calculate the annual quota
      const annualQuota =
        state.commissionRate > 0
          ? Math.round(state.targetCommission / (state.commissionRate / 100))
          : 0;

      // Calculate the updated monthly ramped quotas with the new ramp value
      const monthlyRampedQuotas = calculateMonthlyRampedQuotas(
        annualQuota,
        state.rampMonths1to3,
        state.rampMonths4to6,
        state.rampMonths7to9,
        newValue,
      );

      return {
        ...state,
        rampMonths10to12: newValue,
        monthlyRampedQuotas,
      };
    }

    case "SET_GUARANTEE_MONTHS_1_TO_3": {
      const newValue =
        typeof action.payload === "function"
          ? action.payload(state.guaranteeMonths1to3)
          : action.payload;

      return {
        ...state,
        guaranteeMonths1to3: newValue,
      };
    }

    case "SET_GUARANTEE_MONTHS_4_TO_6": {
      const newValue =
        typeof action.payload === "function"
          ? action.payload(state.guaranteeMonths4to6)
          : action.payload;

      return {
        ...state,
        guaranteeMonths4to6: newValue,
      };
    }

    case "ADD_ACCELERATOR": {
      // Don't add more than 5 accelerators
      if (state.accelerators.length >= 5) {
        return state;
      }

      // Add the new accelerator
      // When adding a new accelerator, it's reasonable to sort immediately
      // (it's not during user input, so won't cause focus issues)
      const newAccelerators = [...state.accelerators, action.payload];
      const sortedAccelerators = newAccelerators.sort(
        (a, b) => a.threshold - b.threshold,
      );

      return {
        ...state,
        accelerators: sortedAccelerators,
      };
    }

    case "REMOVE_ACCELERATOR": {
      // Allow removing all accelerators (empty array is valid)
      // But don't try to remove from an empty array
      if (state.accelerators.length === 0) {
        return state;
      }

      // Remove accelerator at specified index
      const index = action.payload;
      const newAccelerators = [...state.accelerators];
      newAccelerators.splice(index, 1);

      return {
        ...state,
        accelerators: newAccelerators,
      };
    }

    case "UPDATE_ACCELERATOR": {
      const { index, accelerator, shouldSort = false } = action.payload;

      // Don't update if index is out of bounds
      if (index < 0 || index >= state.accelerators.length) {
        return state;
      }

      // Create a new accelerators array with the updated accelerator
      const newAccelerators = [...state.accelerators];
      newAccelerators[index] = {
        ...newAccelerators[index],
        ...accelerator,
      };

      // Only sort on blur (when shouldSort flag is true)
      // This prevents UI jumping around during user input
      const resultAccelerators = shouldSort
        ? [...newAccelerators].sort((a, b) => a.threshold - b.threshold)
        : newAccelerators;

      return {
        ...state,
        accelerators: resultAccelerators,
      };
    }

    case "SET_MONTHLY_PERFORMANCES": {
      const newValue =
        typeof action.payload === "function"
          ? action.payload(state.monthlyPerformances)
          : action.payload;

      return {
        ...state,
        monthlyPerformances: newValue,
      };
    }

    case "UPDATE_OTE": {
      return {
        ...state,
        ote: state.baseSalary + state.targetCommission,
      };
    }

    case "UPDATE_MONTHLY_RAMPED_QUOTAS": {
      // Calculate the annual quota
      const updatedAnnualQuota =
        state.commissionRate > 0
          ? Math.round(state.targetCommission / (state.commissionRate / 100))
          : 0;

      // Calculate the monthly ramped quotas
      const updatedMonthlyRampedQuotas = calculateMonthlyRampedQuotas(
        updatedAnnualQuota,
        state.rampMonths1to3,
        state.rampMonths4to6,
        state.rampMonths7to9,
        state.rampMonths10to12,
      );

      return {
        ...state,
        monthlyRampedQuotas: updatedMonthlyRampedQuotas,
      };
    }

    case "LOAD_STATE_FROM_URL": {
      const newState = {
        ...state,
        ...action.payload,
      };

      // Recalculate monthly ramped quotas if we loaded from URL
      const annualQuota =
        newState.commissionRate > 0
          ? Math.round(
              newState.targetCommission / (newState.commissionRate / 100),
            )
          : 0;

      const monthlyRampedQuotas = calculateMonthlyRampedQuotas(
        annualQuota,
        newState.rampMonths1to3,
        newState.rampMonths4to6,
        newState.rampMonths7to9,
        newState.rampMonths10to12,
      );

      return {
        ...newState,
        ote: newState.baseSalary + newState.targetCommission,
        monthlyRampedQuotas,
      };
    }

    default:
      return state;
  }
}
